import * as Yup from 'yup'
import ReactTooltip from 'react-tooltip'
import { formatInTimeZone, format, toZonedTime } from 'date-fns-tz'
import { startOfDay, endOfDay, addHours } from 'date-fns'
import { v4 as uuid } from 'uuid'

import { isFilesSizeValid } from '../../../helpers/fileValidation'
import Avatar from '../../../Avatar'
import { EMAIL_REGEX } from '../../../helpers/constants'
import { cutTooLong } from '../../../helpers/helpers'
import { getAssetLink } from '../../../helpers/get-asset-link'

const BLOCKED_SCHEDULE_HOURS_AMOUNT = 2
const TIME_SENT_COLUMN_DATE_FORMAT = 'hh:mm a, MM/dd/yyyy'

export const renderSelectBuildingPlaceholder = (buildings, t) => {
  if (!buildings || !buildings.length)
    return t('organisation:communicationPortal.placeholders.selectProperty')
  return buildings.map((building) => building.text).join(', ')
}

export const renderSelectFloorPlaceholder = (floors, t) => {
  if (!floors || !floors.length)
    return t('organisation:communicationPortal.placeholders.selectFloor')
  return floors.map((floor) => floor.text).join(', ')
}

// Note: `t` is optional — this helper is also imported by Surveys/Parameters.jsx, which
// calls it without a translator. Falls back to the untranslated English string there.
export const selectBuildingOptions = (buildings, userRole, t) => {
  if (hasOneBuildingAvailable(buildings, userRole)) {
    return buildings
  }

  return [
    {
      id: -1,
      text: t ? t('organisation:propertyManagers.list.allBuildingsOption') : 'All Buildings',
      avatar: 'building',
      selectAll: true,
    },
    ...buildings,
  ]
}

export const selectFloorOptions = (floors, t) => {
  return [
    {
      id: -1,
      text: t('organisation:communicationPortal.allFloors'),
      avatar: null,
      selectAll: true,
    },
    ...floors,
  ]
}

export const hasOneBuildingAvailable = (buildings, userRole) =>
  userRole === 'PropertyManager' && buildings.length === 1

export const concatTagsWithBuildings = (buildings) => {
  const tagsSet = [...new Set(buildings.flatMap((building) => building.tags))]
  const tags = Array.from(tagsSet, (tag) => ({
    text: tag,
    id: uuid(),
    avatar: 'tagIcon',
    isTag: true,
  }))
  return [...tags, ...buildings]
}

export const splitBuildingsAndTags = (buildings) => {
  return buildings.reduce(
    (acc, item) => {
      if (item.isTag) {
        acc.buildingTags.push(item.text)
      } else {
        acc.buildings.push(item.id)
      }
      return acc
    },
    { buildings: [], buildingTags: [] },
  )
}

export const makeTemplateValidation = (t) => ({
  requiredString: Yup.string()
    .trim()
    .required(t('organisation:communicationPortal.validation.required')),
  requiredNumber: Yup.string()
    .matches(/^\d+$/, t('organisation:communicationPortal.validation.mustBeNumber'))
    .required(t('organisation:communicationPortal.validation.required')),
  requiredEmail: Yup.string()
    .matches(EMAIL_REGEX, t('organisation:communicationPortal.validation.emailInvalid'))
    .required(t('organisation:communicationPortal.validation.required')),
  email: Yup.string().matches(
    EMAIL_REGEX,
    t('organisation:communicationPortal.validation.emailInvalid'),
  ),
})

export const communicationPortalHistoryColumns = (
  mailingListsOptions,
  timezone,
  showDetails,
  t,
) => {
  return [
    {
      Header: t('organisation:communicationPortal.table.type'),
      Cell: (data) => {
        const { row } = data
        return <div className="type-cell">{row.original.type}</div>
      },
    },
    {
      Header: t('organisation:communicationPortal.table.timeSentScheduled'),
      Cell: (data) => {
        const { createdAt, surveyId, status, originalDate } = data.row.original

        if (originalDate) return formatInTimeZone(originalDate, 'UTC', TIME_SENT_COLUMN_DATE_FORMAT)

        return surveyId && surveyId.startDate && surveyId.endDate
          ? `${formatInTimeZone(surveyId.startDate, timezone, TIME_SENT_COLUMN_DATE_FORMAT)} - ${
              status === 'Ongoing'
                ? t('organisation:communicationPortal.table.ongoing')
                : formatInTimeZone(surveyId.endDate, timezone, TIME_SENT_COLUMN_DATE_FORMAT)
            }`
          : `${formatInTimeZone(createdAt, timezone, TIME_SENT_COLUMN_DATE_FORMAT)}`
      },
    },
    {
      id: 'recipients',
      Header: t('organisation:communicationPortal.table.recipients'),
      Cell: (data) => {
        const { recipientTypes = {}, recipientsCount } = data.row.original
        const residentsTypeFilterOptionsList = getResidentsTypeFilterOptions(t)

        const getGroupNames = (groupIds) => {
          if (!groupIds || !groupIds.length) return ''

          const names = groupIds
            .map((id) => {
              const group = residentsTypeFilterOptionsList.find((type) => type.id === id)
              return group ? group.text : ''
            })
            .filter(Boolean)

          return <div className="recipents-cell">{names.length ? `, ${names.join(', ')}` : ''}</div>
        }

        return (
          <div style={{ cursor: 'pointer' }} className="recipents-cell">
            {t('organisation:communicationPortal.table.residentsCount', { count: recipientsCount })}
            {getGroupNames(recipientTypes.userGroups)}
          </div>
        )
      },
    },
    {
      Header: t('organisation:communicationPortal.table.messageTitle'),
      Cell: (data) => {
        const { title } = data.row.original
        return <div className="message-title-cell">{`${cutTooLong(title, 60)}`}</div>
      },
    },
    {
      Header: t('organisation:communicationPortal.table.mailingLists'),
      Cell: (data) => {
        const { row } = data
        if (mailingListsOptions.length) {
          const mailingLists = mailingListsOptions.find((x) => x.id === row.original.mailingList)
          return <div className="mailing-list-cell">{mailingLists ? mailingLists.label : '-'}</div>
        }
        return <div className="mailing-list-cell"></div>
      },
    },
    {
      Header: t('organisation:communicationPortal.table.status'),
      Cell: (data) => {
        const { row } = data
        return (
          <div className={`message-status-filter ${row.original.status}`}>
            {row.original.status}
          </div>
        )
      },
    },
    {
      Header: ' ',
      Cell: (data) => {
        const { row } = data
        return (
          <div className="details-view-button" onClick={() => showDetails(row.original)}>
            {t('organisation:communicationPortal.table.viewDetails')}
          </div>
        )
      },
    },
  ]
}

export const surveyParticipantsTableColumns = (timezone, showFullComment, responses = []) => [
  {
    Header: 'Resident',
    Cell: (data) => {
      const { row } = data
      return (
        <div>
          <Avatar size="medium" style={{ marginRight: '15px' }}>
            <img
              src={getAssetLink({
                path: 'chitown-avatar/avatar',
                file: row.original?.user?.avatar,
                fallback: { file: 'avatar.png' },
              })}
            />
          </Avatar>
          {row.original.user.firstName} {row.original.user.lastName}
        </div>
      )
    },
  },
  {
    Header: '#Apt',
    Cell: (data) => {
      const { row } = data
      return `${row.original.user.apartment}`
    },
  },
  {
    Header: 'Response',
    accessor: 'answers',

    Cell: ({ value = [] }) => {
      if (!value || !value.length) return '-'

      if (value.length > 1) {
        const options = value
          .map((x) => {
            const response = responses.find((y, i) => i === x)
            if (response) return response.option
          })
          .reverse()
        return (
          <div data-tip data-for="answers-tip">
            <span className="ellipsis-text">{options.join(', ')}</span>
            <ReactTooltip className="tooltip" id="answers-tip">
              {options.join(', ')}
            </ReactTooltip>
          </div>
        )
      } else {
        const response = responses.find((x, index) => index === value[0])
        return response ? response.option : '-'
      }
    },
  },
  {
    Header: 'Comment',
    Cell: (data) => {
      const { comment, user } = data.row.original
      const author = `${user.firstName} ${user.lastName}`
      return (
        <div>
          {comment ? (
            <span>
              {cutTooLong(comment, 80)}
              {comment.length > 80 && (
                <span
                  style={{ color: '#A4C8E1', cursor: 'pointer' }}
                  onClick={() => showFullComment(author, comment)}
                >{` See more`}</span>
              )}
            </span>
          ) : (
            '-'
          )}
        </div>
      )
    },
  },
  {
    Header: 'Date',
    Cell: (data) => {
      const { updatedAt } = data.row.original
      return `${formatInTimeZone(updatedAt, timezone, 'MM/dd/yyyy')}`
    },
  },
]

export const getMessageTypeOptions = (t) => [
  {
    id: 0,
    text: t('organisation:communicationPortal.messageTypeOptions.all'),
    value: 'all',
    selectAll: true,
  },
  {
    id: 2,
    text: t('organisation:communicationPortal.messageTypeOptions.email'),
    value: 'email',
  },
  {
    id: 3,
    text: t('organisation:communicationPortal.messageTypeOptions.notification'),
    value: 'notification',
  },
  {
    id: 4,
    text: t('organisation:communicationPortal.messageTypeOptions.sms'),
    value: 'sms',
  },
]

export const getMessageStatusOptions = (t) => [
  {
    id: 0,
    text: t('organisation:communicationPortal.messageStatusOptions.all'),
    value: 'all',
  },
  {
    id: 1,
    text: t('organisation:communicationPortal.messageStatusOptions.scheduled'),
    value: 'scheduled',
  },
  {
    id: 2,
    text: t('organisation:communicationPortal.messageStatusOptions.completed'),
    value: 'completed',
  },
]

export const surveyTypeOptions = [
  {
    id: 0,
    label: 'Satisfaction survey',
    value: 'NPS',
  },
  {
    id: 1,
    label: 'Single choice',
    value: 'Single choice',
  },
  {
    id: 2,
    label: 'Multiple choice',
    value: 'Multiple choice',
  },
]

export const frequencyOptions = [
  {
    id: 0,
    label: 'One time',
    value: 'oneTime',
  },
  {
    id: 1,
    label: 'Ongoing',
    value: 'ongoing',
  },
]

export const responseTypeOptions = [
  {
    id: 0,
    label: 'Smiley faces',
    value: 'smiley',
  },
  {
    id: 1,
    label: 'Thumbs up/down',
    value: 'thumbs',
  },
  {
    id: 2,
    label: '5 point scale',
    value: 'stars',
  },
]

export const surveyStartTriggers = [
  {
    id: 0,
    label: 'Move-in date',
    value: 'moveIn',
  },
  {
    id: 1,
    label: 'Move-out date',
    value: 'moveOut',
  },
  {
    id: 2,
    label: 'Lease start date',
    value: 'leaseStart',
  },
  {
    id: 3,
    label: 'Lease end date',
    value: 'leaseEnd',
  },
  {
    id: 4,
    label: 'After a work order is completed',
    value: 'workOrder',
  },
  {
    id: 5,
    label: 'After a reservation is complete',
    value: 'reservation',
  },
]

export const surveyContinueStartTriggers = [
  {
    id: 0,
    label: 'Before',
    value: 'before',
  },
  {
    id: 1,
    label: 'After',
    value: 'after',
  },
]

export const getSocialLinkServiceOptions = (t) => [
  {
    id: 0,
    label: t('organisation:communicationPortal.socialLinkServiceOptions.google'),
    value: 'google',
  },
  {
    id: 1,
    label: t('organisation:communicationPortal.socialLinkServiceOptions.yelp'),
    value: 'yelp',
  },
  {
    id: 2,
    label: t('organisation:communicationPortal.socialLinkServiceOptions.apartments'),
    value: 'apartments',
  },
  {
    id: 3,
    label: t('organisation:communicationPortal.socialLinkServiceOptions.apartmentRatings'),
    value: 'apartmentratings',
  },
  {
    id: 4,
    label: t('organisation:communicationPortal.socialLinkServiceOptions.facebook'),
    value: 'facebook',
  },
]

// Note: kept untranslated — also imported by src/components/EventManagement's
// useSocialLinksOptions hook chain, which doesn't have a translator threaded through yet.
export const socialLinkServiceOptions = [
  {
    id: 0,
    label: 'Google',
    value: 'google',
  },
  {
    id: 1,
    label: 'Yelp',
    value: 'yelp',
  },
  {
    id: 2,
    label: 'Apartment.com',
    value: 'apartments',
  },
  {
    id: 3,
    label: 'Apartment Ratings',
    value: 'apartmentratings',
  },
  {
    id: 4,
    label: 'Facebook',
    value: 'facebook',
  },
]

export const responseTriggerConditionOptions = [
  {
    id: 0,
    label: 'Highest score',
    value: 'topScore',
  },
  {
    id: 1,
    label: 'Less than highest score',
    value: 'notTopScore',
  },
]

export const textAnswers = [
  {
    title: 'stars',
    collection: ['1 Star', '2 Stars', '3 Stars', '4 Stars', '5 Stars'],
  },
  {
    title: 'smiley',
    collection: ['Sad', 'Neutral', 'Happy', 'Very Happy'],
  },
  {
    title: 'thumbs',
    collection: ['Thumbs Down', 'Thumbs Up'],
  },
]

export const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)

  return result
}

export const grid = 8

export const getListStyle = () => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
})

export const getItemStyle = (draggableStyle) => ({
  marginBottom: `${grid}px`,
  width: '760px',
  padding: '2px 2px',
  border: '1px solid #f1f1f1',
  backgroundColor: '#fff',
  display: 'flex',
  flexDirection: 'row',
  ...draggableStyle,
})

export const getScheduleMinTime = (time, timezone) => {
  const currentDate = toZonedTime(new Date(), timezone)

  const isToday = time && format(time, 'dd MM yyyy') === format(currentDate, 'dd MM yyyy')
  if (isToday) {
    const dateWithAdditionalHours = addHours(currentDate, BLOCKED_SCHEDULE_HOURS_AMOUNT)

    const crossesMidnight = format(time, 'dd') !== format(dateWithAdditionalHours, 'dd')

    return crossesMidnight ? endOfDay(currentDate) : dateWithAdditionalHours
  }

  return startOfDay(currentDate)
}

export const getMessageTypeFilterOptions = (communicateSMSAvailable, t) => {
  const messageTypes = getMessageTypeOptions(t)
  delete messageTypes[0]
  if (!communicateSMSAvailable) {
    delete messageTypes[3]
  }
  return messageTypes.filter(Boolean)
}

export const getPropertyFilterOptions = (ids) =>
  ids.map((id) => ({
    id,
    text: `${id}`,
  }))

export const getRecipientTypesOptions = (t) => [
  {
    id: 0,
    label: t('organisation:communicationPortal.recipientTypesOptions.resident'),
    value: 'residents',
  },
  {
    id: 1,
    label: t('organisation:communicationPortal.recipientTypesOptions.groups'),
    value: 'groups',
  },
  {
    id: 3,
    label: t('organisation:communicationPortal.recipientTypesOptions.unitTypes'),
    value: 'units',
  },
  {
    id: 4,
    label: t('organisation:communicationPortal.recipientTypesOptions.clientTagsLabels'),
    value: 'clientTags',
  },
]

export const pluck = (arr = [], key = 'id') => (arr.length ? arr.map((item) => item[key]) : [])

export const getResidentsTypeFilterOptions = (t) => [
  {
    id: 0,
    text: t('organisation:communicationPortal.residentsTypeFilterOptions.all'),
    value: 'all',
  },
  {
    id: 1,
    text: t('organisation:communicationPortal.residentsTypeFilterOptions.future'),
    value: 'future',
  },
  {
    id: 2,
    text: t('organisation:communicationPortal.residentsTypeFilterOptions.current'),
    value: 'current',
  },
  {
    id: 3,
    text: t('organisation:communicationPortal.residentsTypeFilterOptions.past'),
    value: 'past',
  },
]

export const renderSelectPlaceholder = (options, noOptionsText, t) => {
  if (!options || !options.length)
    return t('organisation:communicationPortal.placeholders.select', { name: noOptionsText })
  return options.map((building) => building.text).join(', ')
}

export const getRecipientsTooltipText = (t) => ({
  residents: t('organisation:communicationPortal.recipientsTooltipText.residents'),
  groups: t('organisation:communicationPortal.recipientsTooltipText.groups'),
  units: t('organisation:communicationPortal.recipientsTooltipText.units'),
  clientTags: t('organisation:communicationPortal.recipientsTooltipText.clientTags'),
})

export const userGroups = [
  {
    id: 1,
    value: 'future',
  },
  {
    id: 2,
    value: 'current',
  },
  {
    id: 3,
    value: 'past',
  },
  {
    id: 4,
    value: 'staff',
  },
]

export const getType = (type) => {
  switch (type) {
    case 'email':
      return 'Email'
    case 'sms':
      return 'SMS'
    case 'notification':
      return 'Push notifications'
    default:
      return null
  }
}

export const getFilesValidationMessage = (files = [], overwriteValidation = false, t) => {
  if (overwriteValidation) {
    return { message: '', isError: false }
  }

  const isFileSizeExceeded = files && !isFilesSizeValid(Array.from(files))
  const isFileFormatWrong = !Array.from(files).every((file) =>
    ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'].includes(file.type),
  )

  if (isFileSizeExceeded) {
    return {
      message: t('organisation:communicationPortal.errors.fileTooLarge'),
      isError: true,
    }
  }

  if (isFileFormatWrong) {
    return { message: t('organisation:communicationPortal.errors.wrongFileFormat'), isError: true }
  }

  return { message: '', isError: false }
}

export const getEmailFromInputOptions = (selectedBuildingName, t) => {
  return [
    { id: 1, value: selectedBuildingName, label: selectedBuildingName },
    { id: 2, value: 'Create new', label: t('organisation:communicationPortal.createNewOption') },
  ]
}
